import {
  Image,
  Pressable,
  Text,
  View,
  type ImageSourcePropType,
} from "react-native";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react-native";
import { EMBER } from "../../onboarding/theme";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export type FactSheetHeaderBadge = {
  label?: string;
  icon?: LucideIcon;
  accent?: boolean;
  color?: string;
  accessibilityLabel?: string;
  onPress?: () => void;
};

type Props = {
  title: string;
  subtitle?: string;
  emoji?: string;
  imageSource?: ImageSourcePropType;
  imageSize?: number;
  visual?: ReactNode;
  badges?: FactSheetHeaderBadge[];
};

export function FactSheetBadge({
  label,
  icon: Icon,
  accent,
  color,
  accessibilityLabel,
  onPress,
}: FactSheetHeaderBadge) {
  const capturePress = usePressCapture("FactSheetBadge");
  const textColor = accent ? "#3F7993" : (color ?? EMBER.colors.textMid);
  const iconColor = accent ? "#3F7993" : (color ?? EMBER.colors.textFaint);
  const content = (
    <>
      {Icon ? <Icon size={10} color={iconColor} strokeWidth={1.5} /> : null}
      {label ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "500",
            color: textColor,
            letterSpacing: 0,
            // Let long sentences wrap within the badge instead of pushing it
            // past the parent card.
            flexShrink: 1,
          }}
        >
          {label}
        </Text>
      ) : null}
    </>
  );
  const getStyle = (pressed = false) => ({
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: Icon ? 5 : 4,
    paddingHorizontal: label ? 10 : 8,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: accent ? "rgba(63, 121, 147, 0.22)" : "rgba(0,0,0,0.08)",
    backgroundColor: accent
      ? pressed
        ? "rgba(63, 121, 147, 0.13)"
        : "rgba(63, 121, 147, 0.08)"
      : pressed
        ? "rgba(0,0,0,0.055)"
        : "rgba(0,0,0,0.02)",
    // Allow the chip to shrink (and its Text to wrap) when the wrapping row
    // doesn't have enough horizontal room for the full label.
    flexShrink: 1,
    maxWidth: "100%" as const,
  });

  if (onPress) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? label}
        onPress={() => {
          capturePress(pressLabel(label, accessibilityLabel), "fact_sheet_nav");
          onPress();
        }}
        style={({ pressed }) => getStyle(pressed)}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <View
      accessibilityLabel={accessibilityLabel ?? label}
      style={getStyle(false)}
    >
      {content}
    </View>
  );
}

export default function FactSheetHeader({
  title,
  subtitle,
  emoji,
  imageSource,
  imageSize = 92,
  visual,
  badges = [],
}: Props) {
  const hasVisual = Boolean(visual || imageSource || emoji);

  return (
    <>
      {hasVisual ? (
        <View
          style={{
            alignItems: "center",
            marginTop: 8,
            marginBottom: 14,
          }}
        >
          {visual ? (
            visual
          ) : imageSource ? (
            <Image
              source={imageSource}
              style={{ width: imageSize, height: imageSize }}
              resizeMode="contain"
            />
          ) : (
            <View
              style={{
                width: 76,
                height: 76,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text
                accessibilityRole="image"
                style={{ fontSize: 56, lineHeight: 64, textAlign: "center" }}
              >
                {emoji}
              </Text>
            </View>
          )}
        </View>
      ) : null}

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
          lineHeight: 28,
          paddingHorizontal: 28,
          paddingTop: hasVisual ? 0 : 16,
        }}
      >
        {title}
      </Text>

      {subtitle ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: EMBER.colors.textLight,
            lineHeight: 20,
            marginTop: 6,
            marginBottom: badges.length ? 12 : 8,
            textAlign: "center",
          }}
        >
          {subtitle}
        </Text>
      ) : null}

      {badges.length ? (
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 6,
            marginTop: subtitle ? 0 : 12,
            marginBottom: 0,
          }}
        >
          {badges.map((badge, index) => (
            <FactSheetBadge
              key={badge.label ?? badge.accessibilityLabel ?? index}
              {...badge}
            />
          ))}
        </View>
      ) : null}
    </>
  );
}
